import { useEffect, useState } from 'react';
import type { CompanyReportSection } from '../useSection';
import throttle from 'lodash/throttle';

const SCROLL_HEIGHT = 900;

export function useScrollLimit({
  initialSection,
  onScrollLimitExceeded,
  enabled = true,
}: {
  initialSection: CompanyReportSection;
  onScrollLimitExceeded: (limitExceeded: boolean, scrollY: number) => void;
  enabled?: boolean;
}) {
  const [limitExceeded, setLimitExceeded] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    function getOffset() {
      const element = document.getElementById(initialSection);
      const body = document.body;
      const documentElement = document.documentElement;
      const scrollTop =
        window.pageYOffset || documentElement.scrollTop || body.scrollTop;
      const clientTop = documentElement.clientTop || body.clientTop || 0;
      const offset =
        (element &&
          element.getBoundingClientRect().top + scrollTop - clientTop) ||
        0;
      return offset;
    }

    function scrollHandler() {
      const offset = getOffset();
      const { scrollY, innerHeight } = window;
      const pixelLimit = SCROLL_HEIGHT;

      const limitTop = offset;
      const limitBottom = offset + pixelLimit;
      const windowTop = scrollY;
      const windowBottom = scrollY + innerHeight;

      const isNowLimitExceeded =
        windowTop > limitBottom || windowBottom < limitTop;
      const withinLimit =
        (windowTop <= limitBottom && windowBottom > limitTop) ||
        (windowBottom >= limitTop && windowTop < limitBottom);

      if (!limitExceeded && isNowLimitExceeded) {
        onScrollLimitExceeded(true, scrollY);
        setLimitExceeded(true);
      } else if (limitExceeded && withinLimit) {
        onScrollLimitExceeded(false, scrollY);
        setLimitExceeded(false);
      }
    }

    const throttledHandler = throttle(scrollHandler, 100, {
      leading: true,
      trailing: true,
    });

    throttledHandler();
    setTimeout(throttledHandler, 800); // Give time for report to load on change
    window.addEventListener('scroll', throttledHandler);

    return () => {
      window.removeEventListener('scroll', throttledHandler);
    };
  }, [enabled, limitExceeded, initialSection, onScrollLimitExceeded]);

  return {
    scrollLimitExceed: limitExceeded,
  };
}
